/**
 * @boli/core - Cart Service
 *
 * Platform-agnostic shopping cart management for Boli Live.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 */

import type {
  Cart,
  CartItem,
  AddToCartRequest,
  UpdateCartItemRequest,
  ApiResponse,
  CheckoutRequest,
  CheckoutResponse,
  GeoLocation,
  Order,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface ICartService {
  /**
   * Get the current user's cart with expanded products
   */
  getCart(): Promise<Cart>;

  /**
   * Add an item to the cart (or update quantity if already exists)
   */
  addItem(data: AddToCartRequest): Promise<ApiResponse<CartItem>>;

  /**
   * Update the quantity of a cart item
   */
  updateItem(itemId: string, data: UpdateCartItemRequest): Promise<ApiResponse<CartItem>>;

  /**
   * Remove a single item from the cart
   */
  removeItem(itemId: string): Promise<ApiResponse<void>>;

  /**
   * Clear all items from the cart
   */
  clearCart(): Promise<ApiResponse<void>>;

  /**
   * Get the number of items in the cart (lightweight)
   */
  getCartCount(): Promise<number>;

  /**
   * Process checkout and create an order
   */
  checkout(data: {
    shippingAddress: GeoLocation;
    paymentMethod: 'wallet' | 'cod' | 'jazzcash' | 'easypaisa';
    notes?: string;
  }): Promise<CheckoutResponse>;

  /**
   * Get user's orders
   */
  getOrders(status?: string): Promise<Order[]>;

  /**
   * Get a single order by ID
   */
  getOrder(orderId: string): Promise<Order | null>;
}

export interface CartServiceConfig {
  apiBaseUrl: string;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_CART_CONFIG: CartServiceConfig = {
  apiBaseUrl: '',
};

// ============================================================================
// CART SERVICE IMPLEMENTATION
// ============================================================================

export class CartService implements ICartService {
  private config: CartServiceConfig;
  protected fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  protected getAuthToken: () => Promise<string | null>;

  constructor(
    config: Partial<CartServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
    getAuthToken: () => Promise<string | null>
  ) {
    this.config = { ...DEFAULT_CART_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
    this.getAuthToken = getAuthToken;
  }

  /**
   * Get authenticated headers
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  /**
   * Get the current user's cart with expanded products
   */
  async getCart(): Promise<Cart> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/cart`,
        { headers }
      );

      if (!response.ok) {
        // Return empty cart on error
        return {
          items: [],
          subtotal: 0,
          shippingTotal: 0,
          pstTotal: 0,
          grandTotal: 0,
          itemCount: 0,
        };
      }

      const data = await response.json();
      return {
        items: data.items || [],
        subtotal: data.subtotal || 0,
        shippingTotal: data.shippingTotal || 0,
        pstTotal: data.pstTotal || 0,
        grandTotal: data.grandTotal || 0,
        itemCount: data.itemCount || 0,
      };
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      return {
        items: [],
        subtotal: 0,
        shippingTotal: 0,
        pstTotal: 0,
        grandTotal: 0,
        itemCount: 0,
      };
    }
  }

  /**
   * Add an item to the cart
   */
  async addItem(data: AddToCartRequest): Promise<ApiResponse<CartItem>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/cart/items`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: responseData.code || 'ADD_FAILED',
            message: responseData.message || 'Failed to add item to cart',
          },
        };
      }

      return {
        success: true,
        data: responseData.item,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Update the quantity of a cart item
   */
  async updateItem(itemId: string, data: UpdateCartItemRequest): Promise<ApiResponse<CartItem>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/cart/items/${itemId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(data),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: responseData.code || 'UPDATE_FAILED',
            message: responseData.message || 'Failed to update cart item',
          },
        };
      }

      return {
        success: true,
        data: responseData.item,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Remove a single item from the cart
   */
  async removeItem(itemId: string): Promise<ApiResponse<void>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/cart/items/${itemId}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      if (!response.ok) {
        const responseData = await response.json();
        return {
          success: false,
          error: {
            code: responseData.code || 'REMOVE_FAILED',
            message: responseData.message || 'Failed to remove item from cart',
          },
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Clear all items from the cart
   */
  async clearCart(): Promise<ApiResponse<void>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/cart`,
        {
          method: 'DELETE',
          headers,
        }
      );

      if (!response.ok) {
        const responseData = await response.json();
        return {
          success: false,
          error: {
            code: responseData.code || 'CLEAR_FAILED',
            message: responseData.message || 'Failed to clear cart',
          },
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Get the number of items in the cart (lightweight)
   */
  async getCartCount(): Promise<number> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/cart/count`,
        { headers }
      );

      if (!response.ok) {
        return 0;
      }

      const data = await response.json();
      return data.count || 0;
    } catch (error) {
      console.error('Failed to get cart count:', error);
      return 0;
    }
  }

  /**
   * Process checkout and create an order
   */
  async checkout(data: {
    shippingAddress: GeoLocation;
    paymentMethod: 'wallet' | 'cod' | 'jazzcash' | 'easypaisa';
    notes?: string;
  }): Promise<CheckoutResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/checkout`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: responseData.code || 'CHECKOUT_FAILED',
            message: responseData.message || 'Checkout failed',
          },
        };
      }

      return {
        success: true,
        order: responseData.order,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Get user's orders
   */
  async getOrders(status?: string): Promise<Order[]> {
    try {
      const headers = await this.getAuthHeaders();
      const url = status
        ? `${this.config.apiBaseUrl}/api/orders?status=${status}`
        : `${this.config.apiBaseUrl}/api/orders`;

      const response = await this.fetchAdapter(url, { headers });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return data.orders || [];
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      return [];
    }
  }

  /**
   * Get a single order by ID
   */
  async getOrder(orderId: string): Promise<Order | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/orders/${orderId}`,
        { headers }
      );

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch order:', error);
      return null;
    }
  }
}

// ============================================================================
// CART SERVICE FACTORY
// ============================================================================

let cartServiceInstance: CartService | null = null;

export function createCartService(
  config: Partial<CartServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
  getAuthToken: () => Promise<string | null>
): CartService {
  cartServiceInstance = new CartService(config, fetchAdapter, getAuthToken);
  return cartServiceInstance;
}

export function getCartService(): CartService {
  if (!cartServiceInstance) {
    throw new Error('CartService not initialized. Call createCartService first.');
  }
  return cartServiceInstance;
}
